import type { ReactElement, FormEvent, ChangeEvent } from "react";

import { useState } from "react";
import axios, { AxiosError } from "axios";
import { X, Mail, Lock, Eye, EyeOff, User, Gift } from "lucide-react";
import { toast } from "sonner";
import Logo from "./Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Auth mode type
 */
type AuthMode = "login" | "register";

/**
 * AuthModal component props
 */
interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Login response from API
 */
interface LoginResponse {
  session_token?: string;
  user?: {
    id: string;
    email: string;
    name: string;
  };
}

/**
 * Register response from API
 */
interface RegisterResponse {
  session_token?: string;
  credits?: number;
  user?: {
    id: string;
    email: string;
    name: string;
  };
}

/**
 * Google OAuth URL response
 */
interface GoogleUrlResponse {
  url: string;
}

/**
 * API error response
 */
interface ApiErrorResponse {
  detail?: string;
}

/**
 * AuthModal Component
 *
 * Modal dialog for login and registration with Google OAuth and email auth.
 */
const AuthModal = ({
  isOpen,
  onClose,
  onSuccess,
}: AuthModalProps): ReactElement | null => {
  const [mode, setMode] = useState<AuthMode>("login");
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [promoCode, setPromoCode] = useState<string>("");

  if (!isOpen) return null;

  const handleGoogleLogin = async (): Promise<void> => {
    try {
      const redirectUri = window.location.origin + "/auth/callback";
      const response = await axios.get<GoogleUrlResponse>(
        `${API}/auth/google/url`,
        {
          params: { redirect_uri: redirectUri },
        }
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to initiate Google login");
      console.error("Google login error:", error);
    }
  };

  const handleEmailLogin = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post<LoginResponse>(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      // Store session token in localStorage
      if (response.data.session_token) {
        localStorage.setItem("session_token", response.data.session_token);
      }
      toast.success("Welcome back!");
      onSuccess?.();
      onClose();
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      toast.error(axiosError.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!email || !password || !name) {
      toast.error("Please fill in all fields");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post<RegisterResponse>(
        `${API}/auth/register`,
        {
          email,
          password,
          name,
          promo_code: promoCode || undefined,
        },
        { withCredentials: true }
      );
      // Store session token in localStorage
      if (response.data.session_token) {
        localStorage.setItem("session_token", response.data.session_token);
      }
      const credits = response.data.credits || 1;
      toast.success(
        `Account created! You have ${credits} story credit${credits > 1 ? "s" : ""}.`
      );
      onSuccess?.();
      onClose();
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      toast.error(axiosError.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = (): void => {
    setEmail("");
    setPassword("");
    setName("");
    setPromoCode("");
  };

  const switchMode = (newMode: AuthMode): void => {
    setMode(newMode);
    resetForm();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-sm mx-4 rounded-2xl shadow-xl animate-slide-up overflow-hidden"
        style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10"
        >
          <X className="w-4 h-4" style={{ color: "var(--st-text-muted)" }} />
        </button>

        <div className="p-6">
          {/* Header */}
          <div className="text-center mb-5">
            <div className="mb-4">
              <Logo size={112} className="mx-auto" />
            </div>
            <h1
              className="text-2xl font-bold mb-1"
              style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
            >
              {mode === "login" ? "Welcome Back" : "Join Tellor"}
            </h1>
            <p
              className="text-sm"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
            >
              {mode === "login"
                ? "Sign in to continue your story journey"
                : "Get 1 free story credit when you sign up!"}
            </p>
          </div>

          {/* Social Login */}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 py-3 mb-4 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-sm"
            style={{
              fontFamily: "Quicksand, sans-serif",
              color: "var(--st-text-heading)",
              border: "1px solid var(--st-border-light)",
              background: "var(--st-bg-surface)",
            }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px" style={{ background: "var(--st-border-light)" }}></div>
            <span
              className="text-xs"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
            >
              or
            </span>
            <div className="flex-1 h-px" style={{ background: "var(--st-border-light)" }}></div>
          </div>

          {/* Email Form */}
          <form
            onSubmit={mode === "login" ? handleEmailLogin : handleRegister}
            className="space-y-3"
          >
            {mode === "register" && (
              <div className="relative">
                <User
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                  style={{ color: "var(--st-text-muted)" }}
                />
                <input
                  type="text"
                  value={name}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setName(e.target.value)
                  }
                  placeholder="Your name"
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    border: "1px solid var(--st-border-light)",
                    background: "var(--st-bg-surface)",
                    color: "var(--st-text-heading)",
                  }}
                />
              </div>
            )}

            <div className="relative">
              <Mail
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: "var(--st-text-muted)" }}
              />
              <input
                type="email"
                value={email}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setEmail(e.target.value)
                }
                placeholder="your@email.com"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  border: "1px solid var(--st-border-light)",
                  background: "var(--st-bg-surface)",
                  color: "var(--st-text-heading)",
                }}
              />
            </div>

            <div className="relative">
              <Lock
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: "var(--st-text-muted)" }}
              />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setPassword(e.target.value)
                }
                placeholder={mode === "register" ? "Min 6 characters" : "Password"}
                className="w-full pl-10 pr-10 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  border: "1px solid var(--st-border-light)",
                  background: "var(--st-bg-surface)",
                  color: "var(--st-text-heading)",
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" style={{ color: "var(--st-text-muted)" }} />
                ) : (
                  <Eye className="w-4 h-4" style={{ color: "var(--st-text-muted)" }} />
                )}
              </button>
            </div>

            {mode === "register" && (
              <div className="relative">
                <Gift
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                  style={{ color: "var(--st-text-muted)" }}
                />
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setPromoCode(e.target.value.toUpperCase())
                  }
                  placeholder="Promo code (optional)"
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    border: "1px solid var(--st-border-light)",
                    background: "var(--st-bg-surface)",
                    color: "var(--st-text-heading)",
                  }}
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg font-semibold text-sm transition-all disabled:opacity-50"
              style={{
                background: "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                color: "white",
                fontFamily: "Quicksand, sans-serif",
              }}
            >
              {loading
                ? mode === "login"
                  ? "Signing in..."
                  : "Creating account..."
                : "Continue"}
            </button>
          </form>

          {/* Switch Mode */}
          <p
            className="text-center text-xs mt-4"
            style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
          >
            {mode === "login" ? (
              <>
                New here?{" "}
                <button
                  type="button"
                  onClick={() => switchMode("register")}
                  className="font-semibold"
                  style={{ color: "var(--st-primary)" }}
                >
                  Create account
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => switchMode("login")}
                  className="font-semibold"
                  style={{ color: "var(--st-primary)" }}
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
